/**
 * Case page: the customer's mail, the quote draft, the one action.
 *
 * The minimal case page (12 Oct 2026, round 2): a sales person who opens a case sees,
 * in this order, the compact mail card with the customer line, the draft as a table in
 * the middle of the screen with one status icon per line and one row action where one
 * is needed, and the one primary action pinned to the viewport bottom. Everything else
 * is one click away in "Details (n)", in a row's chevron or under "More", and the page
 * remembers what a person opened.
 *
 * One column on every width (`data-case-layout="stacked"`): on a laptop the draft is a
 * table, on a phone (below 768 px) it is a list of cards. The work column is the same
 * component the Outlook add-in pane renders (`CaseWork`). A side-by-side mode for wide
 * screens is deferred to round 3; its remembered key `LAYOUT_KEY` is reserved here.
 *
 * This file is the data wiring and the column; the parts live in their own files:
 * `case-header.tsx`, `mail-column.tsx`, `case-work.tsx` (draft lines, primary action,
 * details sheet). Keys: `j` / `k` next and previous case, `a` accept the matched items,
 * `c` confirm the customer, `t` take, `d` mark done and next, `r` copy the reply, `?`
 * the list. In the draft: the arrow keys move, Enter confirms the row, `n` is "No item",
 * `p` opens the price, `q` the quantity, `i` the item search. "Go to the draft" is the
 * first stop of the page for the Tab key.
 *
 * Shadow mode: drafts only. Nothing is sent and nothing is written to F&O.
 */

import { FileSearch, Icon } from "@norican/icons";
import { Button, Card, EmptyState, PageHeader, Skeleton } from "@norican/react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { DocumentTitle } from "@/components/document-title";
import { LiveDataError } from "@/components/live-data-error";
import { CASE_SHORTCUTS, ShortcutHelpDialog } from "@/components/shortcut-help";
import { CaseHeader } from "@/features/case-review/case-header";
import { CaseWork } from "@/features/case-review/case-work";
import { firstDraftLineId, focusDraftRow } from "@/features/case-review/draft-focus";
import { type DraftLayout } from "@/features/case-review/draft-lines";
import { MailColumn } from "@/features/case-review/mail-column";
import { focusNeedles } from "@/features/case-review/mail-focus";
import { type CaseFlow, useCaseFlow } from "@/features/case-review/use-case-flow";
import { useCaseWrites } from "@/features/case-review/use-case-writes";
import { queueHref } from "@/features/queue/queue-defaults";
import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { ShortcutContext, type ShortcutRegistry, useShortcuts } from "@/hooks/use-shortcuts";
import { type CaseQuotation } from "@/lib/api";
import { useCaseDetail } from "@/lib/data";
import { FEATURES } from "@/lib/features";
import { isOutOfScopeCase } from "@/lib/intents";
import { type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import { ACTION_LABELS } from "@/lib/sales-flow";

// The mail card keeps its home for the translation tests and the training stages.
export { SourceCard } from "@/features/case-review/mail-column";

/** The fixture case shown for a bare `/case` in the local dev server only. */
const SAMPLE_CASE_ID = import.meta.env.DEV ? "RFQ-4821" : "";

/**
 * Reserved for round 3: the remembered side-by-side toggle (mail left, table right) on
 * screens from 1440 px. Not read yet; the stacked column is the only layout this round.
 */
export const LAYOUT_KEY = "i2q.case.layout";

/**
 * A quotation for this request is already in D365 and was sent (or confirmed): the
 * primary action then reads "Nothing to do" with "Quoted as ...".
 */
export function quotationExistsSentence(quotations: readonly CaseQuotation[]): string | null {
  const sent = quotations.filter(
    (quotation) => quotation.status === "sent" || quotation.status === "confirmed",
  );
  if (sent.length === 0) {
    return null;
  }
  const names = sent.map((quotation) => `${quotation.number} (${quotation.status})`).join(", ");
  return sent.length === 1
    ? `A quotation for this inquiry exists: ${names}.`
    : `Quotations for this inquiry exist: ${names}.`;
}

/** The last change a reply depends on: the customer confirmation or a line decision. */
export function lastDecisionAt(detail: QuoteCaseDetail): string | null {
  const times = [
    detail.work?.customer_at ?? null,
    ...detail.lines.map((line) => line.decision?.decided_at ?? null),
    // A line correction by sales changes what the reply should say (F08).
    ...detail.lines.map((line) => line.override?.decided_at ?? null),
  ]
    .filter((value): value is string => Boolean(value))
    .filter((value) => !Number.isNaN(new Date(value).getTime()));
  times.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  return times[0] ?? null;
}

function BackToQueue() {
  return (
    <Link to={queueHref()} className="text-accent-fg-subtle text-sm font-semibold hover:underline">
      Go to the queue
    </Link>
  );
}

/**
 * The body of one case: the mail card and the work column, sharing one set of writes
 * (the customer line on the card and the dialogs in the work column act on the same
 * case). Keyed per case by the page: "Copied", the open sheet, the chosen line and an
 * open customer search belong to the case they were made on, never to the next one.
 */
function CaseBody({
  detail,
  flow,
  layout,
}: {
  detail: QuoteCaseDetail;
  flow: CaseFlow;
  layout: DraftLayout;
}) {
  const writes = useCaseWrites(detail);
  const [pickingCustomer, setPickingCustomer] = useState(false);
  // The line a person works on: the mail card marks its text (P28). `reveal` counts the
  // clicks on a row's customer words: those also bring the text into view, so the same
  // row clicked twice scrolls twice.
  const [focus, setFocus] = useState<{ line: QuoteLine | null; reveal: number }>({
    line: null,
    reveal: 0,
  });
  const focusedLine = focus.line;
  return (
    <>
      <MailColumn
        detail={detail}
        focusText={
          focusedLine ? focusNeedles(focusedLine.enquiryText, focusedLine.customerItem) : null
        }
        focusReveal={focus.reveal}
        customer={
          isOutOfScopeCase(detail) ? null : { writes, onFind: () => setPickingCustomer(true) }
        }
      />
      <CaseWork
        detail={detail}
        variant="page"
        layout={layout}
        flow={flow}
        writes={writes}
        pickingCustomer={pickingCustomer}
        onPickingCustomerChange={setPickingCustomer}
        onFocusedLineChange={(line, reveal) =>
          setFocus((prev) => ({ line, reveal: reveal ? prev.reveal + 1 : prev.reveal }))
        }
      />
    </>
  );
}

export interface CaseReviewPageProps {
  /** The draft form, for the render tests (the page reads its width otherwise). */
  layout?: DraftLayout;
}

export function CaseReviewPage({ layout }: CaseReviewPageProps = {}) {
  const { id } = useParams<{ id?: string }>();
  const caseId = id ?? SAMPLE_CASE_ID;
  // Data seam: live case store; the fixture only in the local dev server.
  const { data: detail, isLoading, isError, error } = useCaseDetail(caseId);
  // The walk through the queue ("Next case") and the time on the case.
  const flow = useCaseFlow(caseId);
  // What the parts of the page register for the keys (the action bar).
  const [registry] = useState<ShortcutRegistry>(() => new Map());
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // A phone gets the cards; the server render (media queries false) gets the table.
  const narrow = useMediaQuery(NARROW_QUERY);
  const draftLayout: DraftLayout = layout ?? (narrow ? "list" : "table");
  useShortcuts(
    {
      j: "next",
      k: "previous",
      a: "accept",
      c: "confirmCustomer",
      t: "take",
      d: "doneAndNext",
      r: "copyReply",
      // The row keys act on the row of the draft that holds the focus. Enter on a row is
      // taken by the draft itself (`DraftLines`), before the grid steps into a cell.
      n: "rowNoItem",
      p: "rowPrice",
      q: "rowQuantity",
      i: "rowPick",
      "?": "help",
    },
    { help: () => setShortcutsOpen(true) },
    registry,
    FEATURES.shortcuts && caseId !== "",
  );

  if (!caseId) {
    return (
      <div className="flex flex-col gap-4">
        <DocumentTitle title="Case" />
        <PageHeader title="Case" subtitle="Read the mail, check the draft, do the one action" />
        <Card variant="outline">
          <EmptyState
            className="py-12"
            icon={<Icon icon={FileSearch} size="lg" className="text-fg-subtle" />}
            title="No case open"
            description="Open a mail from the queue to see the customer's mail, the draft I2Q prepared and what to do next."
            actions={<BackToQueue />}
          />
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <DocumentTitle title="Case" />
        <PageHeader title="Case" subtitle="Loading" />
        <Skeleton variant="rect" height={200} />
        <Skeleton variant="rect" height={280} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <DocumentTitle title="Case" />
        <PageHeader title="Case" subtitle={caseId} />
        <LiveDataError what="this case" error={error} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-col gap-4">
        <DocumentTitle title="Case" />
        <PageHeader title="Case" subtitle={caseId} />
        <Card variant="outline">
          <EmptyState
            className="py-12"
            icon={<Icon icon={FileSearch} size="lg" className="text-fg-subtle" />}
            title="Case not found"
            description="This case is not in the case store. Open one from the queue."
            actions={<BackToQueue />}
          />
        </Card>
      </div>
    );
  }

  return (
    <ShortcutContext.Provider value={registry}>
      {/* One tall column, no scroll box of its own: the action bar is sticky to the
          viewport bottom and needs the page to be the scroll container (measured 12 Oct
          2026: a scroll box or a wrapper around the bar put it below the fold). */}
      <div className="flex min-w-0 flex-col gap-4" data-case-layout="stacked">
        {/* The first stop of the page for the keys: past the header and the mail, to the
            first row of the draft. Shown only while it holds the focus. */}
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          className="sr-only focus:not-sr-only focus:w-fit"
          onClick={() => {
            const first = firstDraftLineId();
            if (first) {
              focusDraftRow(first);
            }
          }}
          data-skip-to-draft
        >
          {ACTION_LABELS.goToDraft}
        </Button>
        <CaseHeader detail={detail} flow={flow} />
        {/* One page serves every case of the walk (j / k only navigate), so the body
            remounts per case. */}
        <CaseBody key={detail.id} detail={detail} flow={flow} layout={draftLayout} />
        {shortcutsOpen && (
          <ShortcutHelpDialog open onOpenChange={setShortcutsOpen} rows={CASE_SHORTCUTS} />
        )}
      </div>
    </ShortcutContext.Provider>
  );
}
